/**
 * What an illustration run's banner says, decided from the run's own status
 * (ADR-0085).
 *
 * The bug this exists to prevent: the banner used to decide whether a run had
 * finished by comparing two counts — pictures drawn against pictures asked
 * for — and a run that plans fewer spots than were asked for never makes that
 * comparison true. A wholly successful run of seven pictures, asked for eight,
 * read "Drawing 8 of 8…" for an hour with nothing to click.
 *
 * Two rules, both from ADR-0085:
 *
 * - **Only the run says whether it is over.** `status` is the answer and is
 *   already on the wire; a count says how far, never whether.
 * - **The denominator is what the run planned, not what was asked for.** The
 *   spacing rule drops a chosen paragraph that is out of range or next to one
 *   already taken, so the two are different facts and the server sends both.
 *
 * A run that planned fewer than asked names both numbers and **does not say
 * why**: the shortfall may be the spacing rule dropping spots the model did
 * offer, and the client cannot tell that from a short answer.
 */

export interface RunCounts {
  status: "planning" | "drawing" | "done" | "failed" | "cancelled";
  /** What the author asked for, and was priced and authorized for. */
  wanted: number;
  /** Spots the plan produced. Zero until the run is drawing. */
  planned: number;
  done: number;
  failed: number;
}

export type RunLine =
  /** Still reading the chapter: no denominator exists yet. */
  | { kind: "planning" }
  /** Working: the nth picture of what the run planned. */
  | { kind: "drawing"; at: number; of: number }
  /** Settled, and the plan gave the run everything it asked for. */
  | { kind: "drawn"; drawn: number; planned: number }
  /** Settled with fewer spots planned than asked for: both numbers, no cause. */
  | { kind: "short"; drawn: number; planned: number; wanted: number }
  /** Stopped by somebody (ADR-0167): what it had placed stays. */
  | { kind: "stopped"; drawn: number }
  /** Settled with nothing drawn. The reason is shown under the line. */
  | { kind: "none" };

export function runLine(run: RunCounts): RunLine {
  if (run.status === "planning") return { kind: "planning" };

  // Fall back to what was asked for only if a drawing run somehow has no spot
  // rows. The plan job writes them all before it enqueues the first, so this
  // cannot happen — but a denominator of zero would read as "Drawing 1 of 0…".
  const of = run.planned || run.wanted;

  if (run.status === "drawing") {
    // Settled spots, not drawn ones: a spot that failed still moved the run
    // on, and counting only the drawn ones would stall the number on a run
    // that is visibly still working.
    return { kind: "drawing", at: Math.min(run.done + run.failed + 1, of), of };
  }
  // Before "none": a stopped run that drew nothing was stopped, not refused,
  // and it may have no plan at all, so it names no denominator.
  if (run.status === "cancelled") return { kind: "stopped", drawn: run.done };
  if (run.done === 0) return { kind: "none" };
  if (of < run.wanted) {
    return { kind: "short", drawn: run.done, planned: of, wanted: run.wanted };
  }
  return { kind: "drawn", drawn: run.done, planned: of };
}
